import { Modal, Spin } from "antd";
import { showError, showSuccess } from "common/ToastNotifications";
import { ProhibitedArea } from "components";
import { ThemeCustomizer } from "components/molecules/ThemeCustomizer";
import { Dispatch, useCallback, useEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Action } from "redux-saga";
import actions from "store/actions";
import { AppState } from "store/AppState";

const mapStateToProps = (state: AppState) => ({
    customizationData: state.tenancyCustomization.customizationData,
    deleteCustomizationResult: state.tenancyCustomization.deleteCustomizationResult,
    createCustomizationResult: state.tenancyCustomization.createCustomizationResult,
    updateCustomizationResult: state.tenancyCustomization.updateCustomizationResult,
    loadingCustomizationData: state.tenancyCustomization.loading,
    user: state.userLogin.user,
});

const mapDispatchToProps = (dispatch: Dispatch<Action<string>>) => ({
    getTenancyCustomization: () => dispatch(actions.getTenancyCustomizationRequest()),
    refreshTenancyCustomization: () => dispatch(actions.refreshTenancyCustomizationRequest()),
    createTenancyCustomization: (data: ICustomizationData) => dispatch(actions.createTenancyCustomizationRequest(data)),
    updateTenancyCustomization: (data: ICustomizationData) => dispatch(actions.updateTenancyCustomizationRequest(data)),
    deleteTenancyCustomization: () => dispatch(actions.deleteTenancyCustomizationRequest()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type IPropsFromRedux = ConnectedProps<typeof connector>;

interface TenancyCustomizationProps extends IPropsFromRedux {}

const TenancyCustomization: React.FC<TenancyCustomizationProps> = ({
    customizationData,
    deleteCustomizationResult,
    createCustomizationResult,
    updateCustomizationResult,
    refreshTenancyCustomization,
    createTenancyCustomization,
    updateTenancyCustomization,
    deleteTenancyCustomization,
    user,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const lastSaveOpRef = useRef<"create" | "update" | null>(null);
    const isFirstDeleteResultRender = useRef(true);

    useEffect(() => {
        if (isFirstDeleteResultRender.current) {
            isFirstDeleteResultRender.current = false;
            return;
        }
        if (deleteCustomizationResult?.success === true) {
            setIsModalOpen(false);
            setIsLoading(false);
            showSuccess("Theme reset to default");
            refreshTenancyCustomization();
        } else if (deleteCustomizationResult?.success === false) {
            setIsLoading(false);
            showError("Failed to reset. Please try again.");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteCustomizationResult]);

    useEffect(() => {
        if (createCustomizationResult?.success === true) {
            refreshTenancyCustomization();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createCustomizationResult]);

    useEffect(() => {
        if (updateCustomizationResult?.success === true) {
            refreshTenancyCustomization();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateCustomizationResult]);

    const handleSave = useCallback(
        (data: ICustomizationData) => {
            const { logoSize: _logoSize, ...rest } = data as any;
            const submissionData: any = { ...rest };
            if (customizationData?.version != null) {
                submissionData.version = customizationData.version;
            }
            if (customizationData) {
                lastSaveOpRef.current = "update";
                updateTenancyCustomization(submissionData);
            } else {
                lastSaveOpRef.current = "create";
                createTenancyCustomization({
                    ...(customizationData || {}),
                    ...submissionData,
                });
            }
        },
        [customizationData, createTenancyCustomization, updateTenancyCustomization],
    );

    const saveResult = lastSaveOpRef.current === "create" ? createCustomizationResult : lastSaveOpRef.current === "update" ? updateCustomizationResult : null;

    const handleResetToDefault = useCallback(() => setIsModalOpen(true), []);

    const handleDelete = useCallback(() => {
        setIsLoading(true);
        deleteTenancyCustomization();
    }, [deleteTenancyCustomization]);

    const handleCancel = useCallback(() => setIsModalOpen(false), []);

    return (
        <>
            {!user || user.userType !== "ACCOUNT_ADMIN" ? (
                <ProhibitedArea />
            ) : (
                <ThemeCustomizer onSave={handleSave} onResetToDefault={handleResetToDefault} isDataAvail={!!customizationData} showPreview saveResult={saveResult} />
            )}
            <Modal title="Are you sure?" open={isModalOpen} onOk={handleDelete} onCancel={handleCancel}>
                <p>Warning: This will reset the data, and all existing data will not be retrievable.</p>
                {isLoading && <Spin className="text-center" />}
            </Modal>
        </>
    );
};

export default connector(TenancyCustomization);
